import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatMoneyUSD } from '../../utils/functions';

interface TooltipData {
  name: string;
  value: number;
  color: string;
}

interface ChartTooltipProps {
  seriesIndex: number;
  dataPointIndex: number;
  w: any;
  chartViewMode: 'day' | 'week';
  t?: (key: string) => string;
}

export const generateChartTooltip = ({
  seriesIndex,
  dataPointIndex,
  w,
  chartViewMode,
  t = (key: string) => key, // Default to returning the key if no translation function provided
}: ChartTooltipProps): string => {
  const isDark = document.documentElement.classList.contains('dark');
  const currentSeries = w.globals.initialSeries[seriesIndex];
  const currentPoint = currentSeries.data[dataPointIndex];

  if (!currentPoint) return '';

  const currentX = w.globals.seriesX[seriesIndex][dataPointIndex];

  const dateDisplay =
    chartViewMode === 'day'
      ? format(new Date(currentX), 'dd MMM', { locale: es })
      : `${t('Semana')} ${w.globals.categoryLabels[dataPointIndex]}`;

  const allSeriesData = w.globals.initialSeries
    .map((series: any, index: number) => {
      const seriesX = w.globals.seriesX[index];
      const pointIndex = seriesX.findIndex((x: any) => x === currentX);
      const yValue = pointIndex >= 0 ? series.data[pointIndex]?.y : null;

      return yValue !== null && yValue !== undefined
        ? {
            name: series.name,
            value: yValue,
            color: series.color,
          }
        : null;
    })
    .filter((item: TooltipData | null): item is TooltipData => item !== null)
    .sort((a: TooltipData, b: TooltipData) => b.value - a.value);

  const labelColor = isDark ? '#A1A1AA' : '#666';
  const valueColor = isDark ? '#ECEDEE' : '#000000';
  const bgColor = isDark ? '#1a1a2e' : '#f8f9fa';
  const shadow = isDark ? '0 2px 4px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.1)';
  const titleColor = isDark ? '#ECEDEE' : '#333';

  const rows = allSeriesData
    .map(
      (item: TooltipData) => `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
          <div style="display: flex; align-items: center;">
            <div style="
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background-color: ${item.color};
              margin-right: 8px;
            "></div>
            <span style="color: ${labelColor};">${item.name}:</span>
          </div>
          <span style="font-weight: bold; color: ${valueColor}; margin-left: 10px;">
            ${formatMoneyUSD(item.value, { decimals: 2 })}
          </span>
        </div>
      `
    )
    .join('');

  return `
    <div style="
      background-color: ${bgColor};
      border-radius: 8px;
      box-shadow: ${shadow};
      padding: 12px;
      font-family: Arial, sans-serif;
      min-width: 200px;
    ">
      <div style="text-align: center; font-weight: bold; margin-bottom: 8px; color: ${titleColor};">
        ${dateDisplay}
      </div>
      <div style="padding-top: 8px;">
        ${rows}
      </div>
    </div>
  `;
};
